import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { BookDemoProvider } from "./hooks/useBookDemo";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import TestimonialsGrid from "./components/TestimonialsGrid";
import ProblemSection from "./components/ProblemSection";
import PlatformSection from "./components/PlatformSection";
import BuiltForFuture from "./components/BuiltForFuture";
import BenefitsSection from "./components/BenefitsSection";
import ExperienceSection from "./components/ExperienceSection";
import CTABanner from "./components/CTABanner";
import Footer from "./components/Footer";

// Lazy load page components for better performance
const EducationalInstitution = lazy(() => import("./pages/EducationalInstitution"));
const ForEnterprises = lazy(() => import("./pages/ForEnterprises"));
const OurStory = lazy(() => import("./pages/OurStory"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AboutUs = lazy(() => import("./pages/AboutUs"));

// ScrollToTop component - on route change, scroll to a #hash element if present,
// otherwise scroll to the top of the page.
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Give the lazy-loaded page a moment to mount before scrolling.
      const timer = setTimeout(() => {
        const el = document.getElementById(hash.replace("#", ""));
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        window.scrollTo(0, 0);
      }, 200);
      return () => clearTimeout(timer);
    }

    // Immediate scroll
    window.scrollTo(0, 0);

    // Delayed scroll to handle async rendering
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);

    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return null;
}

import SEO from "./components/SEO";

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="AI Learning Platform for Universities & Enterprises"
        description="Elevante.ai is the leading hands-on AI learning platform. 50+ lab templates, AI courses across disciplines, industry simulations, and customisable enterprise training programs for real AI adoption."
        keywords="AI learning platform, AI training, AI courses for universities, AI upskilling enterprises, hands-on AI labs, AI curriculum, machine learning courses, AI skilling, AI adoption, corporate AI training, AI education, AI for higher education"
        canonical="https://elevante.ai"
      />
      <Navbar />
      <Hero />
      <TestimonialsGrid />
      <ProblemSection />
      <PlatformSection />
      <BuiltForFuture />
      <BenefitsSection />
      <ExperienceSection />
      <CTABanner />
      <Footer />
    </div>
  );
};

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#8942FE] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <BookDemoProvider>
            <ScrollToTop />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/educational-institution" element={<EducationalInstitution />} />
                <Route path="/for-enterprises" element={<ForEnterprises />} />
                <Route path="/our-story" element={<OurStory />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/about" element={<AboutUs />} />
              </Routes>
            </Suspense>
          </BookDemoProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
